import { CacheService } from "@app/services/cache.Service";
import { IDateQueryOptions } from "@services/accelerometerRecord.service";
import { DisplayService, IDialogOptionsJson } from "@services/display.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import * as angular from "angular";
import * as moment from "moment";
import { AlertRuleStatus } from "src/app/main/modules/alerts/_enums/AlertRuleStatus.enums";
import { AlcolockAlertSubCategory, AlertCategory, AlertPreferenceType, AlertState, AxleWeightProfileAlertSubCategory, FaultCodeAlertSubCategory, GeofenceAndZoneAlertSubCategory, InputAlertSubCategory, PowerEventAlertSubCategory, RecipientType, ResourceFilterType, TachoAlertSubCategory, TemperatureProfileAlertSubCategory, TollAlertSubCategory, TranscanAlertSubCategory, TyreAlertSubCategory, VehicleAlertSubCategory, VideoEventSubCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertResolutionCode } from "src/app/main/modules/alerts/_models/IAlertResolutionCode";
import { IAlertRuleGroupMapping, IAlertRecordMetadata, IAlertRule, IAlertRuleRecipient, IAlertRuleRecord, INotification, INotificationQuota, INotificationRecord } from "../_models/alerts.interfaces";

export interface IAlertCategoryTrigger {
    enumName: string;
    enum: any;
    subCategories?: { [key: number]: boolean };
    categoryAllowedDelayResend?: boolean;
    subCategoriesAllowedDelayResend?: { [key: number]: boolean };
    showReasonCodes?: { [key: number]: boolean };
    labels?: { [key: number]: string };
    subCategoriesUnits?: { [key: number]: number };
}

export interface IShowAlertEditorOptions {
    autoLoadedRecordId?: number;

    onAlertEditorCancelled?: () => void;
    onAlertEditorDeleted?: () => void;
    onAlertEditorSaved?: () => void;
}

interface IAlertDateParameters {
    from: string;
    to: string;
}

export enum AlertRecordMaterialisationOptions {
    Basic = 0,
    WithSerialisedFields = 1,
    WithMetadata = 2,

    All = ~0
}

export interface IAlertRecordQueryOptions extends IDateQueryOptions {
    MaterialisationOptions: AlertRecordMaterialisationOptions;
    Id?: number;
    AlertRuleId?: number;
    AccountId?: number;
    IsActive?: boolean;
    RequiresReview?: boolean;
    IgnoreOffRoadVehicles?: boolean;
    AlertState?: AlertState;
    VehicleId?: number;
    SecondaryId?: string;
    LastSentStart?: Date;
    LastSentEnd?: Date;
}

export interface IAlertRuleQueryOptions {
    AccountId?: number;
    Category?: AlertCategory;
    IsActive?: boolean;
    Status?: AlertRuleStatus;
    RequiresReview?: boolean;
    ResendAlerts?: boolean;
}

export interface IAlertResolutionCodeQueryOptions extends IDateQueryOptions {
    Id?: number;
    Name?: string;
    AccountId?: number;
    Active?: boolean;
}

export class AlertsService {
    private activeAlertsCacheName = "activeAlertsCache";
    static $inject = ["$q", "$http", "cacheService", "displayService", "translationService", "unitConversionService", "$sanitize"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly cacheService: CacheService,
        private readonly displayService: DisplayService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly $sanitize: any) {
        this.cacheService.createCache(this.activeAlertsCacheName, { maxAge: 30 * 60 * 1000 });
    }

    getNotificationQuotas(monthToCheck: Date): angular.IPromise<INotificationQuota> {

        return this
            .$http({
                url: `/api/notifications/quotas`,
                method: "GET",
                params: {
                    monthToCheck: monthToCheck.toISOString()
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<INotificationQuota>) => {
                return response.data;
            });
    }

    getNotificationQuotasUsed(monthToCheck: moment.Moment): angular.IPromise<IAlertRuleRecord[]> {
        return this
            .$http({
                url: `/api/notifications/quotasUsed`,
                method: "GET",
                params: {
                    monthToCheck: monthToCheck.format()
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord[]>) => {
                return response.data;
            });
    }

    getAlertRuleRecord(id: number, materialisationOptions: AlertRecordMaterialisationOptions): angular.IPromise<IAlertRuleRecord> {

        const queryOptions: IAlertRecordQueryOptions = { Id: id, MaterialisationOptions: materialisationOptions };

        return this
            .$http({
                url: `/api/alertrecords`,
                method: "GET",
                params: queryOptions
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord[]>) => {

                if (!response.data.length) {
                    return null;
                }

                return response.data[0];
            });
    }

    getAlertRuleRecordsUsingQueryOptions(queryOptions: IAlertRecordQueryOptions): angular.IPromise<IAlertRuleRecord[]> {

        if (queryOptions.Start) {
            queryOptions.Start = new Date(queryOptions.Start).toISOString();
        }

        if (queryOptions.End) {
            queryOptions.End = new Date(queryOptions.End).toISOString();
        }

        return this
            .$http({
                url: `/api/alertrecords`,
                method: "GET",
                params: queryOptions
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord[]>) => {
                return response.data;
            });
    }

    getAlertRuleRecords(ruleId: number, dateFrom: moment.Moment, dateTo: moment.Moment): angular.IPromise<IAlertRuleRecord[]> {
        const dateParameters: IAlertDateParameters = this.getDateRange(dateFrom, dateTo);

        return this
            .$http({
                url: `/api/alerts/rules/${ruleId}/history`,
                method: "GET",
                params: dateParameters
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord[]>) => {
                return response.data;
            });
    }

    getAlertRuleRecordsForAccount(dateFrom: moment.Moment | Date, dateTo: moment.Moment | Date): angular.IPromise<IAlertRuleRecord[]> {
        const dateParameters: IAlertDateParameters = this.getDateRange(dateFrom, dateTo);

        return this
            .$http({
                url: `/api/alerts/history`,
                method: "GET",
                params: dateParameters
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord[]>) => {
                return response.data;
            });
    }



    getAlertRuleRecordById(recordId: number): angular.IPromise<IAlertRuleRecord> {

        return this
            .$http({
                url: `/api/alerts/history/${recordId}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleRecord>) => {
                return response.data;
            });
    }

    getAlertRule(ruleId: number): angular.IPromise<IAlertRule> {
        if (!ruleId) {
            return;
        }

        return this
            .$http({
                url: `/api/alerts/rules/${ruleId}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRule>) => {
                var rule = response.data;

                if (rule) {
                    this.prepareRuleForDisplay(rule)
                }

                return rule;
            });
    }

    getAlertRules(queryOptions?: IAlertRuleQueryOptions): angular.IPromise<IAlertRule[]> {

        return this
            .$http({
                url: `/api/alerts/rules`,
                method: "GET",
                params: queryOptions
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRule[]>) => {
                // process response
                var rules = response.data;

                rules?.forEach(rule => {
                    this.prepareRuleForDisplay(rule);
                });

                return rules;
            });
    }

    getActiveAlertRules(): angular.IPromise<IAlertRule[]> {
        return this.cacheService.getOrRetrieveData<IAlertRule[]>(this.activeAlertsCacheName, () => this.getActiveAlertRulesFromDb(), false);
    }

    getActiveAlertRulesFromDb(): angular.IPromise<IAlertRule[]> {

        return this
            .$http({
                url: `/api/alerts/activeRules`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRule[]>) => {
                // process response
                var rules = response.data;

                rules?.forEach(rule => {
                    this.prepareRuleForDisplay(rule);
                });

                return rules;
            });
    }

    getAlertNotifications(ruleId: number, alertRuleRecordId: number): angular.IPromise<INotification[]> {
        return this
            .$http({
                url: `/api/alerts/rules/${ruleId}/history/${alertRuleRecordId}/notifications`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<INotification[]>) => {
                return response.data;
            });
    }

    getAlertNotificationRecords(notificationId: number): angular.IPromise<INotificationRecord[]> {
        return this
            .$http({
                url: `/api/notifications/records/${notificationId}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<INotificationRecord[]>) => {
                return response.data;
            });
    }

    getAlertResolutionCodes(queryOptions?: IAlertResolutionCodeQueryOptions): angular.IPromise<IAlertResolutionCode[]> {
        return this
            .$http({
                url: `/api/alertresolutioncodes`,
                method: "GET",
                params: queryOptions
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertResolutionCode[]>) => {
                return response.data;
            });

    }

    addAlertResolutionCode(code: IAlertResolutionCode): angular.IPromise<IAlertResolutionCode> {
        return this
            .$http({
                url: `/api/alertresolutioncodes`,
                method: "POST",
                data: code
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertResolutionCode>) => {
                return response.data;
            });
    }

    updateAlertResolutionCode(code: IAlertResolutionCode): angular.IPromise<IAlertResolutionCode> {

        return this
            .$http({
                url: `/api/alertresolutioncodes/${code.Id}`,
                method: "POST",
                data: code
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertResolutionCode>) => {
                return response.data;
            });
    }

    getAlertRulesContainsGroupId(groupId: number): angular.IPromise<IAlertRuleGroupMapping> {

        return this
            .$http({
                url: `/api/alerts/rules/groupId/${groupId}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRuleGroupMapping>) => {
                return response.data;
            });
    }

    showAlertEditor(alertRule: IAlertRule, $event: MouseEvent, options?: IShowAlertEditorOptions) {

        const dialogTitle = alertRule
            ? this.translationService.t(`Alerts.alertRuleName`, alertRule?.Name)
            : this.translationService.t(`Alerts.newAlertRule`);

        const dialogOptions: IDialogOptionsJson = {
            title: dialogTitle,
            dialogHTML: `<alert-editor ` +
                `auto-loaded-record-id="${options?.autoLoadedRecordId}" ` +
                `on-alert-editor-cancelled="$ctrl.onAlertEditorCancelled(); $ctrl.hide();" ` +
                `on-alert-editor-deleted="$ctrl.onAlertEditorDeleted(); $ctrl.hide();" ` +
                `on-alert-editor-saved="$ctrl.onAlertEditorSaved(); $ctrl.hide();" ` +
                `class="dialog-mode" ` +
                `alert-rule="$ctrl.alertRule"></alert-editor>`,
            dialogId: `alertEditorComponent`,
            customClass: `alert-editor-dialog`,
            bindings: {
                alertRule: alertRule,
                onAlertEditorCancelled: () => { options?.onAlertEditorCancelled() },
                onAlertEditorDeleted: () => { options?.onAlertEditorDeleted(); },
                onAlertEditorSaved: () => { options?.onAlertEditorSaved(); },
            },
            event: $event
        };

        return this.displayService.showDialog(dialogOptions);
    }

    deleteRule(rule: IAlertRule) {
        this.prepareRuleForSaving(rule);

        return this
            .$http({
                url: `/api/alerts/rules/${rule.Id}`,
                data: rule,
                method: "DELETE"
            }).then(() => {
                this.cacheService.deleteItem(this.activeAlertsCacheName, rule, (i1, i2) => i1.Id == i2.Id);
            });
    }

    saveRule(rule: IAlertRule) {
        this.prepareRuleForSaving(rule);

        return this
            .$http({
                url: `/api/alerts/rules/`,
                data: rule,
                method: "POST"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAlertRule>) => {
                const alertRule = response.data;
                this.prepareRuleForDisplay(alertRule);
                alertRule.Status != AlertRuleStatus.Deleted && this.cacheService.addOrUpdateItem<IAlertRule>(this.activeAlertsCacheName, alertRule, (i1, i2) => i1.Id == i2.Id);

                return alertRule;
            });
    }

    setSmsNumber(recipient: IAlertRuleRecipient): void {
        if (!recipient.Custom || recipient.Custom.trim().length < 1) {
            return;
        }

        const customCode = recipient.Custom.replace("^\++", "");
        const smsParts = customCode.split(" ");
        const countryDialCode = recipient.countryDialCode ? recipient.countryDialCode.toString() : "";
        const plusCountryDialCode = "+" + countryDialCode;

        if (smsParts.length > 1 && smsParts[0] === countryDialCode) {
            return;
        }

        if (smsParts.length > 1 && smsParts[0].length < 4) {
            const remaining = recipient.Custom.slice(smsParts[0].length).trim();
            recipient.Custom = plusCountryDialCode + " " + (remaining.startsWith("0") ? remaining.slice(1) : remaining);
            return;
        }

        if (customCode.startsWith(countryDialCode)) {
            recipient.Custom = plusCountryDialCode + (smsParts.length === 1 ? " " : "") + customCode.slice(countryDialCode.length).trim();
            return;
        }

        if (customCode.startsWith("0") && recipient.countryDialCode && customCode.length > 1) {
            recipient.Custom = plusCountryDialCode + " " + customCode.slice(1).trim();
            return;
        }

        if (smsParts.length === 1) {
            recipient.Custom = plusCountryDialCode + " " + customCode.trim();
            return;
        }

        recipient.Custom = plusCountryDialCode + " " + customCode.slice(smsParts[0].length).trim();
    }

    setCountryDialCode(recipient: IAlertRuleRecipient): void {
        if (!recipient.Custom) {
            return;
        }
        const smsParts = recipient.Custom.split(" ");
        if (smsParts.length > 1) {
            if (!isNaN(+smsParts[0])) {
                recipient.countryDialCode = +smsParts[0];
            }
        }
    }

    createAlertTriggerMappings(): IAlertCategoryTrigger[] {
        const alertCategoryTriggers: IAlertCategoryTrigger[] = [];

        Object.keys(AlertCategory).forEach(k => {
            const index: number = Number(k);

            if (isNaN(index)) {
                return;
            }

            switch (index as AlertCategory) {

                case AlertCategory.Geofence:
                    alertCategoryTriggers[index] = {
                        enumName: `GeofenceAndZoneAlertSubCategory`,
                        enum: GeofenceAndZoneAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                case AlertCategory.Vehicle:
                    const speedUnitSuffix = this.unitConversionService.getConversionLabel(UnitConversionType.Speed);

                    alertCategoryTriggers[index] = {
                        enumName: `VehicleAlertSubCategory`,
                        enum: VehicleAlertSubCategory,
                        subCategories: {
                            [VehicleAlertSubCategory.Speeding]: true,
                            [VehicleAlertSubCategory.FuelLevelChange]: true,
                            [VehicleAlertSubCategory.ExcessiveDwell]: true
                        },
                        subCategoriesAllowedDelayResend: {
                            [VehicleAlertSubCategory.Speeding]: true,
                            [VehicleAlertSubCategory.FuelLevelChange]: true,
                            [VehicleAlertSubCategory.ExcessiveDwell]: true
                        },
                        showReasonCodes: {
                            [VehicleAlertSubCategory.PlotReason]: true,
                        },
                        labels: {
                            [VehicleAlertSubCategory.Speeding]: this.translationService.t(`AlertEditor.TriggerSpeedValue`),
                            [VehicleAlertSubCategory.ContextualSpeeding]: this.translationService.t(`AlertEditor.overCertain`, speedUnitSuffix),
                            [VehicleAlertSubCategory.FuelLevelChange]: this.translationService.t(`AlertEditor.TriggerVolumeValue`),
                            [VehicleAlertSubCategory.ExcessiveDwell]: this.translationService.t(`AlertEditor.duration`)
                        },
                        subCategoriesUnits: {
                            [VehicleAlertSubCategory.Speeding]: AlertPreferenceType.DistanceMeasurement,
                            [VehicleAlertSubCategory.ContextualSpeeding]: AlertPreferenceType.DistanceMeasurement,
                            [VehicleAlertSubCategory.FuelLevelChange]: AlertPreferenceType.VolumeMeasurement
                        }

                    };

                    break;

                case AlertCategory.Toll:
                    alertCategoryTriggers[index] = {
                        enumName: `TollAlertSubCategory`,
                        enum: TollAlertSubCategory
                    };

                    break;

                case AlertCategory.Alcolock:
                    alertCategoryTriggers[index] = {
                        enumName: `AlcolockAlertSubCategory`,
                        enum: AlcolockAlertSubCategory
                    };

                    break;

                case AlertCategory.Tyres:
                    alertCategoryTriggers[index] = {
                        enumName: `TyreAlertSubCategory`,
                        enum: TyreAlertSubCategory
                    };

                    break;

                case AlertCategory.FaultCode:
                    alertCategoryTriggers[index] = {
                        enumName: `FaultCodeAlertSubCategory`,
                        enum: FaultCodeAlertSubCategory
                    };

                    break;

                case AlertCategory.Tachograph:
                    alertCategoryTriggers[index] = {
                        enumName: `TachoAlertSubCategory`,
                        enum: TachoAlertSubCategory,
                        subCategoriesAllowedDelayResend: {
                            [TachoAlertSubCategory.DrivingWithoutTachoCard]: true
                        }
                    };

                    break;

                case AlertCategory.Inputs:
                    alertCategoryTriggers[index] = {
                        enumName: `InputAlertSubCategory`,
                        enum: InputAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                case AlertCategory.TemperatureProfile:
                    alertCategoryTriggers[index] = {
                        enumName: `TemperatureProfileAlertSubCategory`,
                        enum: TemperatureProfileAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                case AlertCategory.Transcan:
                    alertCategoryTriggers[index] = {
                        enumName: `TranscanAlertSubCategory`,
                        enum: TranscanAlertSubCategory
                    };

                    break;

                case AlertCategory.CameraEvent:
                    alertCategoryTriggers[index] = {
                        enumName: `VideoEventSubCategory`,
                        enum: VideoEventSubCategory
                    };

                    break;

                case AlertCategory.Zone:
                    alertCategoryTriggers[index] = {
                        enumName: `GeofenceAndZoneAlertSubCategory`,
                        enum: GeofenceAndZoneAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                case AlertCategory.AxleWeightProfile:
                    alertCategoryTriggers[index] = {
                        enumName: `AxleWeightProfileAlertSubCategory`,
                        enum: AxleWeightProfileAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                case AlertCategory.PowerEvent:
                    alertCategoryTriggers[index] = {
                        enumName: `PowerEventAlertSubCategory`,
                        enum: PowerEventAlertSubCategory,
                        categoryAllowedDelayResend: true
                    };

                    break;

                default:

                    break;
            }
        });

        return alertCategoryTriggers;
    }

    parseAlertRecordMetadata(alertRecord: IAlertRuleRecord): void {

        if (!alertRecord.AlertMetadataJson) {
            return;
        }

        alertRecord.alertMetadata = JSON.parse(alertRecord.AlertMetadataJson) as IAlertRecordMetadata;

        if (alertRecord.alertMetadata.PlotId) {
            alertRecord.plot = { Id: alertRecord.alertMetadata.PlotId }
        }
    }

    getAlertRuleSubcategoryEnum(category: AlertCategory): { subCategoryEnum, subCategoryString } {
        switch (category) {
            case 0:
            case 14:
                return { subCategoryEnum: GeofenceAndZoneAlertSubCategory, subCategoryString: 'GeofenceAndZoneAlertSubCategory' };
            case 1:
                return { subCategoryEnum: VehicleAlertSubCategory, subCategoryString: 'VehicleAlertSubCategory' };
            case 5:
                return { subCategoryEnum: TollAlertSubCategory, subCategoryString: 'TollAlertSubCategory' };
            case 6:
                return { subCategoryEnum: AlcolockAlertSubCategory, subCategoryString: 'AlcolockAlertSubCategory' };
            case 7:
                return { subCategoryEnum: TyreAlertSubCategory, subCategoryString: 'TyreAlertSubCategory' };
            case 8:
                return { subCategoryEnum: InputAlertSubCategory, subCategoryString: 'InputAlertSubCategory' };
            case 9:
                return { subCategoryEnum: FaultCodeAlertSubCategory, subCategoryString: 'FaultCodeAlertSubCategory' };
            case 10:
                return { subCategoryEnum: TachoAlertSubCategory, subCategoryString: 'TachoAlertSubCategory' };
            case 11:
                return { subCategoryEnum: TemperatureProfileAlertSubCategory, subCategoryString: 'TemperatureProfileAlertSubCategory' };
            case 12:
                return { subCategoryEnum: TranscanAlertSubCategory, subCategoryString: 'TranscanAlertSubCategory' };
            case 13:
                return { subCategoryEnum: VideoEventSubCategory, subCategoryString: 'VideoEventSubCategory' };
            case 15:
                return { subCategoryEnum: AxleWeightProfileAlertSubCategory, subCategoryString: 'AxleWeightProfileAlertSubCategory' };
            case 16:
                return { subCategoryEnum: PowerEventAlertSubCategory, subCategoryString: 'PowerEventAlertSubCategory' };
            default:
                break;
        }
    }


    // ---

    private prepareRuleForDisplay(rule: IAlertRule): void {
        if (rule.Name) {
            rule.Name = this.$sanitize(rule.Name);
        }

        rule.Recipients.forEach(recipient => {
            if (recipient.UserId) {
                recipient.type = RecipientType.User;
            } else if (recipient.GroupId) {
                recipient.type = RecipientType.Group;
            } else {
                recipient.type = RecipientType.Custom;
            }
        });

        rule.Filters.forEach(filter => {
            if (filter.All) {
                filter.filterType = ResourceFilterType.Any;
            }
            else if (filter.None) {
                filter.filterType = ResourceFilterType.None;
            }
            else if (filter.GroupId) {
                filter.filterType = ResourceFilterType.Group;
            }
            else if (filter.ResourceId) {
                filter.filterType = ResourceFilterType.Specific;
            }
        });
    }

    private prepareRuleForSaving(rule: IAlertRule) {
        rule.Filters.forEach(filter => {
            // Use "==" not "===" as this has come from the front end as a string
            if (filter.filterType == ResourceFilterType.None) {
                filter.None = true;
                filter.GroupId = null;
                filter.ResourceId = null;
                filter.All = false;
            }
            else if (filter.filterType == ResourceFilterType.Any) {
                filter.All = true;
            }
        });
    }

    private getDateRange(startDate: Date | moment.Moment, endDate?: Date | moment.Moment): IAlertDateParameters {

        const startMoment = moment(startDate).startOf("day");
        const endMoment = moment(endDate || startDate).startOf("day");

        endMoment.add(1, "day");

        const params: IAlertDateParameters = {
            from: startMoment.utc().format(),
            to: endMoment.utc().format()
        };

        return params;
    }
}
